import { SEATS, type Meld, type Seat, type Tile } from '../game/types.js';
import { seatPosition, type LayoutConfig } from '../layout/types.js';
import {
  DISCARD_TILE_GAP_PX,
  MELD_STRIP_GAP_PX,
  MELD_STRIP_THICKNESS_PX,
  TILE_HEIGHT,
  TILE_WIDTH,
} from '../utils/constants.js';
import { drawMelds } from './HandRenderer.js';
import type { RenderPalette } from './palette.js';
import { drawTileFace, type TileRotation } from './TileRenderer.js';
import { gridSlots, pondWrapFrom, splitPondCell } from './tileLayout.js';

/** Sideways seats occupy `height × width` on screen once rotated. */
function isSideways(rotation: TileRotation): boolean {
  return rotation === 90 || rotation === 270;
}

/**
 * Every seat's discard pile + called melds, each drawn face-up in that
 * seat's pond cell, oriented toward its seat. `splitPondCell` carves the
 * meld strip off the edge nearest that seat's own rack — a called set
 * renders there, "under" the discards rather than beside the concealed
 * hand — and the remaining area wraps the discard pile into centred rows.
 * Discards render in the order they were thrown — they are NOT sorted; the
 * sequence is meaningful game history. Discarded tiles render at the SAME
 * size as hand tiles (no down-scale) — a played tile shouldn't visually
 * shrink. Which cell a seat gets is `localSeat`-relative: the viewer's own
 * pile always sits nearest their own rack (see `seatPosition`).
 */
export function drawDiscardPond(
  ctx: CanvasRenderingContext2D,
  layout: LayoutConfig,
  discards: readonly (readonly Tile[])[],
  melds: readonly (readonly Meld[])[],
  palette: RenderPalette,
  localSeat: Seat,
): void {
  const w = TILE_WIDTH;
  const h = TILE_HEIGHT;

  for (const seat of SEATS) {
    const position = seatPosition(seat, localSeat);
    const rotation = layout.seatZones[position].rotation;
    const { meldCell, discardCell } = splitPondCell(
      layout.pondSeats[position],
      position,
      MELD_STRIP_THICKNESS_PX,
      MELD_STRIP_GAP_PX,
    );

    drawMelds(ctx, meldCell, melds[seat] ?? [], palette, rotation);

    const pile = discards[seat] ?? [];
    if (pile.length === 0) continue;
    const sideways = isSideways(rotation);
    const fpX = sideways ? h : w;
    const fpY = sideways ? w : h;
    // Left/right seats stack their discards DOWN their side of the table
    // instead of running a wide horizontal row.
    const axis = sideways ? 'vertical' : 'horizontal';
    const slots = gridSlots(
      discardCell,
      pile.length,
      fpX,
      fpY,
      DISCARD_TILE_GAP_PX,
      axis,
      pondWrapFrom(position),
    );
    for (let i = 0; i < slots.length; i += 1) {
      const slot = slots[i];
      const tile = pile[i];
      if (slot === undefined || tile === undefined) continue;
      drawTileFace(
        ctx,
        { x: slot.cx - w / 2, y: slot.cy - h / 2, width: w, height: h, rotation },
        tile,
        palette,
      );
    }
  }
}
